import * as React from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { toast } from 'sonner'
import {
  DataTable,
  sortableColumns,
  type ColumnDefWithLabel as BaseColumnDefWithLabel,
} from '@/components/DataTable'
import { setOrDeleteParam } from '@/utils/router'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSessionsQuery } from '../hooks/useSessionsQuery'
import { isRetryableSessionStatus } from '../retryableStatuses'
import {
  fetchSessionTypeSlugs,
  fetchSessionAttempts,
  retrySession,
  updateSessionStatus,
} from '../api'
import type {
  SessionRecord,
  SessionsQueryParams,
  SessionStatus,
  SessionAttemptRecord,
} from '../types'
import type { FilterCondition, FilterFieldConfig } from '@/utils/filterUtils'
import { SessionAttemptsSubRowsTable } from './SessionAttemptsSubRowsTable'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { useCapability } from '@/features/permissions/hooks'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { getSessionVariant } from '../sessionVariant'

const UnavailableData = ({
  className = 'text-gray-400',
}: {
  className?: string
}) => <span className={className}>-</span>

const statusColors: Record<SessionStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  user_completed: 'bg-green-100 text-green-800',
}

const STATUS_LABELS: Record<SessionStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  user_completed: 'User Completed',
}

type ColumnDefWithLabel = BaseColumnDefWithLabel<SessionRecord>

const columnsWithLabels: ColumnDefWithLabel[] = [
  {
    accessorKey: 'id',
    label: 'Session ID',
    cell: ({ row }) => {
      const id = row.original.id
      return <SessionIdCell sessionId={id} />
    },
  },
  {
    accessorKey: 'slug',
    label: 'Type',
    cell: ({ row }) => {
      const workflowSlug = row.original.slug
      const variant = getSessionVariant(row.original.metadata)
      if (!workflowSlug) {
        return <UnavailableData />
      }
      // Format slug to readable name: "appt-booking" -> "Appt Booking"
      const formattedName = workflowSlug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      return (
        <div className="flex max-w-xs items-baseline gap-2 truncate">
          <span className="truncate">{formattedName}</span>
          {variant && (
            <code className="shrink-0 font-mono text-xs text-gray-500">
              {variant}
            </code>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'status',
    label: 'Status',
    cell: ({ row }) => {
      const status = row.original.status
      return (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
          {STATUS_LABELS[status] ?? status}
        </span>
      )
    },
  },
  {
    accessorKey: 'num_attempts',
    label: 'Attempts',
    cell: ({ row }) => {
      const numAttempts = row.original.num_attempts
      if (numAttempts === undefined || numAttempts === null) {
        return <UnavailableData />
      }
      return (
        <div className="whitespace-nowrap text-right w-full">{numAttempts}</div>
      )
    },
  },
  {
    accessorKey: 'token_usage',
    label: 'Tokens',
    cell: ({ row }) => {
      const tokens = row.original.token_usage
      if (tokens === undefined || tokens === null) {
        return <UnavailableData />
      }
      return (
        <div className="whitespace-nowrap text-right w-full">
          {tokens.toLocaleString('en-US')}
        </div>
      )
    },
  },
  {
    accessorKey: 'token_cost',
    label: 'Cost',
    cell: ({ row }) => {
      const cost = row.original.token_cost
      if (cost === undefined || cost === null) {
        return <UnavailableData />
      }
      return (
        <div className="whitespace-nowrap text-right w-full">
          ${cost.toFixed(4)}
        </div>
      )
    },
  },
  {
    accessorKey: 'patient_id',
    label: 'Client ID',
    cell: ({ row }) => {
      const patientId = row.original.patient_id
      return patientId ? (
        <div className="group inline-flex items-center gap-1">
          <div className="font-mono text-xs truncate" title={patientId}>
            {patientId}
          </div>
          <CopyButton
            content={patientId}
            label="Client ID"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      ) : (
        <UnavailableData />
      )
    },
  },
  {
    accessorKey: 'duty_id',
    label: 'Duty ID',
    cell: ({ row }) => {
      const dutyId = row.original.duty_id
      return dutyId ? <DutyIdCell dutyId={dutyId} /> : <UnavailableData />
    },
  },
  {
    accessorKey: 'created_at',
    label: 'Created',
    cell: ({ row }) => {
      const raw = row.original.created_at
      if (!raw) {
        return <UnavailableData />
      }

      try {
        const date = new Date(raw)
        if (Number.isNaN(date.getTime())) {
          return <UnavailableData />
        }

        const formatted = date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })

        return <div className="whitespace-nowrap">{formatted}</div>
      } catch {
        return <UnavailableData />
      }
    },
  },
]

function SessionRowActions({ session }: { session: SessionRecord }) {
  const queryClient = useQueryClient()
  const { getToken } = useAuth()
  // Offering retry on a status Nebula cannot requeue just produces a 502.
  const canRetry = isRetryableSessionStatus(session.status)

  return (
    <div className="flex items-center gap-1">
      {/* Retry */}
      {canRetry && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={(event) => {
            event.stopPropagation()
            void (async () => {
              try {
                const token = await getToken()
                await retrySession(token, session.id)
                await queryClient.invalidateQueries({ queryKey: ['sessions'] })
                toast.success('Session retry started')
              } catch (error) {
                console.error('Failed to retry session', error)
                toast.error(
                  error instanceof Error
                    ? error.message
                    : 'Failed to retry session'
                )
              }
            })()
          }}>
          <span className="sr-only">Retry session</span>
          <span className="text-xs font-semibold">↻</span>
        </Button>
      )}
      {/* Mark user_completed */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={(event) => {
          event.stopPropagation()
          void (async () => {
            try {
              const token = await getToken()
              await updateSessionStatus(token, session.id, 'user_completed')
              await queryClient.invalidateQueries({ queryKey: ['sessions'] })
              toast.success('Session marked as user completed')
            } catch (error) {
              console.error('Failed to update session to user_completed', error)
              toast.error('Failed to mark as user completed')
            }
          })()
        }}>
        <span className="sr-only">Mark as user completed</span>
        <span className="text-xs font-semibold">✓</span>
      </Button>
    </div>
  )
}

function DutyIdCell({ dutyId }: { dutyId: string }) {
  const { toTask } = useAppNavigate()
  return (
    <div className="group inline-flex items-center gap-1">
      <Button
        variant="link"
        className="px-0 h-auto font-normal font-mono text-xs truncate"
        onClick={(event) => {
          event.stopPropagation()
          toTask(`/duties/${dutyId}`)
        }}
        title={dutyId}>
        {dutyId}
      </Button>
      <CopyButton
        content={dutyId}
        label="Duty ID"
        className="opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>
  )
}

function SessionIdCell({ sessionId }: { sessionId: string }) {
  const { toTask } = useAppNavigate()

  return (
    <div className="group inline-flex items-center gap-1">
      <Button
        variant="link"
        className="p-0 h-auto font-normal font-mono text-xs truncate"
        onClick={(event) => {
          event.stopPropagation()
          // No manual query copy: this list's filters belong to the sessions
          // tab, not to the session detail one. toTask carries the shell keys.
          toTask(`/sessions/${sessionId}`)
        }}
        title={sessionId}>
        {sessionId}
      </Button>
      <CopyButton
        content={sessionId}
        label="Session ID"
        className="opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>
  )
}

// Status options for filter dropdown
const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'user_completed', label: 'User Completed' },
]

const decodeFilterFromQueryValue = (rawValue: string) => {
  const values = rawValue
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

  if (values.length === 0) {
    return { operator: '=' as const, value: '' }
  }

  const allNegated = values.every((value) => value.startsWith('-'))
  if (values.length > 1) {
    const decodedValue = values
      .map((value) => (value.startsWith('-') ? value.slice(1) : value))
      .join(',')
    if (allNegated) {
      return { operator: 'not_in' as const, value: decodedValue }
    }
    return { operator: 'in' as const, value: decodedValue }
  }

  const onlyValue = values[0]!
  if (onlyValue.startsWith('-')) {
    return { operator: '!=' as const, value: onlyValue.slice(1) }
  }
  return { operator: '=' as const, value: onlyValue }
}

export function AllWorkflowRunsListView() {
  const navigate = useNavigate()
  const { getToken } = useAuth()
  const [searchParams] = useSearchParams()

  // Keep searchParams in a ref to access the latest value inside callbacks without needing to add it to dependencies
  const searchParamsRef = React.useRef(searchParams)
  searchParamsRef.current = searchParams

  const [sessionTypes, setSessionTypes] = React.useState<string[]>([])
  const [selectedSessions, setSelectedSessions] = React.useState<
    SessionRecord[]
  >([])
  const [showRetryConfirm, setShowRetryConfirm] = React.useState(false)
  const [isRetrying, setIsRetrying] = React.useState(false)

  // Initialize query params from URL or defaults
  const [queryParams, setQueryParams] = React.useState<SessionsQueryParams>(
    () => {
      const params: SessionsQueryParams = {
        limit: 100,
        sort_by: searchParams.get('sort_by') || 'created_at',
        sort_order:
          (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc',
      }

      // Restore filters from URL
      const status = searchParams.get('status')
      const slug = searchParams.get('slug')
      const sessionId = searchParams.get('session_id')
      const patientId = searchParams.get('patient_id')
      const dutyId = searchParams.get('duty_id')

      if (status) params.status = status as SessionStatus
      if (slug) params.slug = slug
      if (sessionId) params.session_id = sessionId
      if (patientId) params.patient_id = patientId
      if (dutyId) params.duty_id = dutyId

      return params
    }
  )

  // Super-user toggle for surfacing internal sessions. Persisted as
  // `show_internal=true` in the URL so bookmarks stick. Default off —
  // even super-users see the clean customer view unless they flip it.
  const [showInternal, setShowInternal] = React.useState<boolean>(
    () => searchParams.get('show_internal') === 'true'
  )

  // Fetch session types on mount
  React.useEffect(() => {
    const loadSessionTypes = async () => {
      try {
        const token = await getToken()
        const types = await fetchSessionTypeSlugs(token)
        setSessionTypes(types)
      } catch (error) {
        console.error('Failed to fetch session types:', error)
      }
    }
    void loadSessionTypes()
  }, [getToken])

  // Sessions flagged `internal` are hidden by a server-side filter:
  // `include_internal=true` goes out only when the viewer holds
  // `sessions.showInternal` AND has the toolbar toggle flipped on.
  //
  // Soft trust boundary — a motivated caller could pass the param
  // themselves — acceptable because the "leak" is just our internal
  // test/authoring session count. The server-side check is tracked in
  // ClickUp plan 2ky4rcma-21113.
  const canShowInternal = useCapability('sessions.showInternal') === true
  const isGlobalAdmin = useIsGlobalAdmin()

  // Build filter field configurations dynamically
  const sessionsFilterFields: FilterFieldConfig[] = React.useMemo(() => {
    const sessionTypeOptions = sessionTypes.map((slug) => ({
      value: slug,
      label: slug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    }))

    return [
      { value: 'id', label: 'Session ID', type: 'text' },
      {
        value: 'slug',
        label: 'Type',
        type: 'options',
        options: sessionTypeOptions,
      },
      {
        value: 'status',
        label: 'Status',
        type: 'options',
        options: statusOptions,
      },
      { value: 'patient_id', label: 'Client ID', type: 'text' },
      { value: 'duty_id', label: 'Duty ID', type: 'text' },
      { value: 'created_at', label: 'Created', type: 'date' },
    ]
  }, [sessionTypes])

  const effectiveQueryParams = React.useMemo(
    () => ({
      ...queryParams,
      include_internal: canShowInternal && showInternal,
    }),
    [queryParams, canShowInternal, showInternal]
  )

  const handleShowInternalChange = React.useCallback(
    (next: boolean) => {
      setShowInternal(next)
      const updatedSearchParams = new URLSearchParams(
        searchParamsRef.current.toString()
      )
      setOrDeleteParam(
        updatedSearchParams,
        'show_internal',
        next ? 'true' : undefined
      )
      navigate({ search: updatedSearchParams.toString() }, { replace: true })
    },
    [navigate]
  )
  // Pass null as sessionType to fetch all sessions
  const queryResult = useSessionsQuery(null, effectiveQueryParams)

  const tableColumns: ColumnDef<SessionRecord>[] = React.useMemo(() => {
    // Dollar cost is GlobalAdmin-only; the API also nulls token_cost for
    // other roles, so hiding the column avoids a dash-filled dead column.
    const visibleColumns = isGlobalAdmin
      ? columnsWithLabels
      : columnsWithLabels.filter(
          (col) => !('accessorKey' in col) || col.accessorKey !== 'token_cost'
        )
    return [
      ...sortableColumns(visibleColumns),
      {
        id: 'actions',
        header: () => <div className="text-left">Actions</div>,
        enableSorting: false,
        cell: ({ row }) => <SessionRowActions session={row.original} />,
      } as ColumnDefWithLabel,
    ]
  }, [isGlobalAdmin])
  const paginationTotalCount = React.useMemo(() => {
    const firstPage = queryResult.data?.pages?.[0] as
      | { pagination?: { total_count?: number; total?: number } }
      | undefined
    const totalCount = firstPage?.pagination?.total_count
    if (typeof totalCount === 'number' && Number.isFinite(totalCount)) {
      return totalCount
    }
    const legacyTotal = firstPage?.pagination?.total
    if (typeof legacyTotal === 'number' && Number.isFinite(legacyTotal)) {
      return legacyTotal
    }
    return null
  }, [queryResult.data?.pages])

  // Build initial filters from URL params for DataTable UI
  const initialFilters = React.useMemo(() => {
    const filters: FilterCondition[] = []

    if (queryParams.status) {
      const decoded = decodeFilterFromQueryValue(queryParams.status)
      filters.push({
        id: `filter-status-${Date.now()}`,
        field: 'status',
        operator: decoded.operator,
        value: decoded.value,
      })
    }

    if (queryParams.slug) {
      const decoded = decodeFilterFromQueryValue(queryParams.slug)
      filters.push({
        id: `filter-slug-${Date.now()}`,
        field: 'slug',
        operator: decoded.operator,
        value: decoded.value,
      })
    }

    if (queryParams.session_id) {
      const decoded = decodeFilterFromQueryValue(queryParams.session_id)
      filters.push({
        id: `filter-id-${Date.now()}`,
        field: 'id',
        operator: decoded.operator,
        value: decoded.value,
      })
    }

    if (queryParams.patient_id) {
      const decoded = decodeFilterFromQueryValue(queryParams.patient_id)
      filters.push({
        id: `filter-patient_id-${Date.now()}`,
        field: 'patient_id',
        operator: decoded.operator,
        value: decoded.value,
      })
    }

    if (queryParams.duty_id) {
      const decoded = decodeFilterFromQueryValue(queryParams.duty_id)
      filters.push({
        id: `filter-duty_id-${Date.now()}`,
        field: 'duty_id',
        operator: decoded.operator,
        value: decoded.value,
      })
    }

    return filters
  }, [
    queryParams.status,
    queryParams.slug,
    queryParams.session_id,
    queryParams.patient_id,
    queryParams.duty_id,
  ])

  // Convert DataTable filter array to individual query parameters
  const handleQueryParamsChange = React.useCallback(
    (params: any) => {
      const newParams: SessionsQueryParams = {
        limit: params.limit,
        sort_by: params.sort_by,
        sort_order: params.sort_order,
        cursor: params.cursor,
      }

      // Extract filter values from the filters array
      if (params.filters && Array.isArray(params.filters)) {
        params.filters.forEach((filter: any) => {
          if (filter.value) {
            switch (filter.field) {
              case 'status':
                newParams.status = filter.value as SessionStatus
                break
              case 'slug':
                newParams.slug = filter.value
                break
              case 'id':
                newParams.session_id = filter.value
                break
              case 'patient_id':
                newParams.patient_id = filter.value
                break
              case 'duty_id':
                newParams.duty_id = filter.value
                break
            }
          }
        })
      }

      setQueryParams(newParams)

      // Update URL search params while preserving unrelated params (e.g. agent, view).
      const updatedSearchParams = new URLSearchParams(
        searchParamsRef.current.toString()
      )
      updatedSearchParams.set('sort_by', newParams.sort_by || 'created_at')
      updatedSearchParams.set('sort_order', newParams.sort_order || 'desc')

      setOrDeleteParam(updatedSearchParams, 'status', newParams.status)
      setOrDeleteParam(updatedSearchParams, 'slug', newParams.slug)
      setOrDeleteParam(updatedSearchParams, 'session_id', newParams.session_id)
      setOrDeleteParam(updatedSearchParams, 'patient_id', newParams.patient_id)
      setOrDeleteParam(updatedSearchParams, 'duty_id', newParams.duty_id)

      navigate({ search: updatedSearchParams.toString() }, { replace: true })
    },
    [navigate]
  )

  const hasAttemptSubRows = React.useCallback(
    (row: SessionRecord) => (row.num_attempts ?? 0) > 0,
    []
  )

  const fetchSessionAttemptsForRow = React.useCallback(
    async (row: SessionRecord): Promise<SessionAttemptRecord[]> => {
      const token = await getToken()
      const response = await fetchSessionAttempts(token, row.id)
      return response.data
    },
    [getToken]
  )

  const renderSessionAttemptsSubRows = React.useCallback(
    (subRows: SessionAttemptRecord[], row: SessionRecord) => {
      return (
        <SessionAttemptsSubRowsTable
          subRows={subRows}
          sessionId={row.id}
          searchParamsString={searchParams.toString()}
          onAttemptClick={navigate}
        />
      )
    },
    [navigate, searchParams]
  )

  const handleBulkRetry = React.useCallback(async () => {
    if (selectedSessions.length === 0) return

    // Sending a status Nebula cannot requeue just returns a 502, which the
    // counts below would report as an indistinguishable failure. Skipping
    // those up front lets the toast name them instead.
    const retryable = selectedSessions.filter((session) =>
      isRetryableSessionStatus(session.status)
    )
    const skippedCount = selectedSessions.length - retryable.length
    if (retryable.length === 0) {
      toast.error(
        `None of the ${skippedCount} selected sessions can be retried. Only completed, failed, or cancelled sessions can.`
      )
      return
    }

    setIsRetrying(true)
    try {
      const token = await getToken()
      const results = await Promise.allSettled(
        retryable.map((session) => retrySession(token, session.id))
      )
      const failedCount = results.filter(
        (result) => result.status === 'rejected'
      ).length
      const succeededCount = results.length - failedCount
      const skippedSuffix =
        skippedCount > 0
          ? ` Skipped ${skippedCount} that cannot be retried.`
          : ''

      if (failedCount === 0) {
        toast.success(
          `Retry requested for ${succeededCount} sessions.${skippedSuffix}`
        )
      } else if (succeededCount === 0) {
        toast.error(`Failed to retry selected sessions.${skippedSuffix}`)
      } else {
        toast.error(
          `Retry requested for ${succeededCount} sessions. Failed for ${failedCount}.${skippedSuffix}`
        )
      }

      await queryResult.refetch?.()
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to retry selected sessions.'
      toast.error(message)
    } finally {
      setIsRetrying(false)
      setShowRetryConfirm(false)
    }
  }, [getToken, queryResult, selectedSessions])

  const handleSelectedRowsChange = React.useCallback((rows: any[]) => {
    const next = rows as SessionRecord[]
    setSelectedSessions((prev) => {
      if (prev.length === next.length) {
        const prevIds = new Set(prev.map((session) => session.id))
        const isSameSelection = next.every((session) => prevIds.has(session.id))
        if (isSameSelection) return prev
      }
      return next
    })
  }, [])

  return (
    <>
      <DataTable
        key="sessions-all"
        showFilters={true}
        queryResult={queryResult}
        onQueryParamsChange={handleQueryParamsChange}
        columns={tableColumns}
        enableMultiSelect
        onSelectedRowsChange={handleSelectedRowsChange}
        toolbarActions={
          <>
            {selectedSessions.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">Actions</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowRetryConfirm(true)}>
                    Retry
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {canShowInternal ? (
              <div className="flex items-center gap-2">
                <Switch
                  id="show-internal-toggle"
                  checked={showInternal}
                  onCheckedChange={handleShowInternalChange}
                />
                <Label
                  htmlFor="show-internal-toggle"
                  className="text-sm font-normal cursor-pointer">
                  Show internal
                </Label>
              </div>
            ) : null}
          </>
        }
        getRowKey={(row: SessionRecord) => row.id}
        canExpandRow={(row) => hasAttemptSubRows(row as SessionRecord)}
        fetchSubRows={(row) => fetchSessionAttemptsForRow(row as SessionRecord)}
        renderSubRows={(subRows, row) =>
          renderSessionAttemptsSubRows(
            subRows as SessionAttemptRecord[],
            row as SessionRecord
          )
        }
        refreshQueryKey={['sessions']}
        totalCount={paginationTotalCount}
        filterFieldConfigs={sessionsFilterFields}
        initialFilters={initialFilters}
      />

      <AlertDialog open={showRetryConfirm} onOpenChange={setShowRetryConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Retry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to retry {selectedSessions.length} sessions?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRetrying}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isRetrying} onClick={handleBulkRetry}>
              {isRetrying ? 'Retrying...' : 'Retry'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
